import React, { useState, useEffect } from "react";
import {
    Dialog, DialogContent, DialogActions,
    Box, Typography, Table, TableHead, TableBody, TableRow, TableCell
} from "@mui/material";
import { axios_tenant } from "../../../../axios";
import Button from "./atoms/Button";

const BASE_URL = "/collaboration/workflow";

/**
 * TrackChangesModal — Displays the full audit history for a task.
 * Pixel-perfect alignment with the Figma "Track Changes / Case History" mockup.
 */
const TrackChangesModal = ({ open, onClose, taskId }) => {
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [taskMeta, setTaskMeta] = useState(null);

    useEffect(() => {
        if (!open || !taskId) return;
        let cancelled = false;
        setLoading(true);

        axios_tenant.get(`${BASE_URL}/tasks/${taskId}/history`)
            .then(res => {
                if (cancelled) return;
                setHistory(res.data.history || []);
                setTaskMeta(res.data.task || null);
            })
            .catch(err => {
                if (cancelled) return;
                console.error("Failed to load history:", err);
                setHistory([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [open, taskId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        // Format as DD/MM/YYYY to match Figma "11/05/2026"
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const HEADER_CELLS = [
        { label: "Changed On", width: 130 },
        { label: "Changed By", width: 170 },
        { label: "Field", width: 130 },
        { label: "Old Value", width: 150 },
        { label: "New Value", width: 150 },
        { label: "Action/Notes", width: 150 }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "10px",
                    maxHeight: "85vh",
                    boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
                    bgcolor: "#ffffff",
                    overflow: "hidden"
                }
            }}
        >
            {/* Header Title & Subtitle with white background as requested */}
            <Box sx={{ px: 3, pt: 3, pb: 1, bgcolor: "#ffffff" }}>
                <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#0F172A" }}>
                    Track Changes / Case History
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 0.5 }}>
                    Task-level history showing what changed, who changed it, and when.
                </Typography>
            </Box>

            {/* Content Area containing Meta Boxes and Table */}
            <DialogContent sx={{ p: 0, bgcolor: "#ffffff", display: "flex", flexDirection: "column" }}>

                {/* Meta Info Box Grid (light grey/blue boxes with subtle borders to match main page background) */}
                {taskMeta && !loading && (
                    <Box sx={{ display: "flex", gap: 2, px: 3, pt: 2, pb: 3, bgcolor: "#ffffff" }}>
                        <Box sx={{ flex: 1, border: "1px solid #E2E8F0", borderRadius: "6px", px: 2, py: 1.5, bgcolor: "#f3f8fc" }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#475569", textTransform: "none", mb: 0.5 }}>
                                Task ID
                            </Typography>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0F172A" }}>
                                {taskMeta.taskId}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, border: "1px solid #E2E8F0", borderRadius: "6px", px: 2, py: 1.5, bgcolor: "#f3f8fc" }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#475569", textTransform: "none", mb: 0.5 }}>
                                Application/Patent No.
                            </Typography>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0F172A" }}>
                                {taskMeta.applicationNo || "—"}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, border: "1px solid #E2E8F0", borderRadius: "6px", px: 2, py: 1.5, bgcolor: "#f3f8fc" }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#475569", textTransform: "none", mb: 0.5 }}>
                                Current Status
                            </Typography>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0F172A" }}>
                                {taskMeta.currentStatus}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <Typography sx={{ color: "#64748B", fontSize: "14px" }}>Loading case history...</Typography>
                    </Box>
                ) : history.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <Typography sx={{ color: "#94A3B8", fontSize: "14px" }}>
                            No changes recorded yet.
                        </Typography>
                    </Box>
                ) : (
                    /* Table Container: Centered/padded inside a premium rounded grey bordered box exactly like Figma! */
                    <Box sx={{ px: 3, pb: 3, bgcolor: "#ffffff" }}>
                        <Box sx={{ border: "1px solid #E2E8F0", borderRadius: "8px", overflow: "hidden" }}>
                            <Table size="medium" sx={{ tableLayout: "fixed", width: "100%" }}>
                                <TableHead>
                                    <TableRow>
                                        {HEADER_CELLS.map(cell => (
                                            <TableCell
                                                key={cell.label}
                                                sx={{
                                                    backgroundColor: "#f3f8fc",
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    color: "#475569",
                                                    width: cell.width,
                                                    borderBottom: "1px solid #E2E8F0",
                                                    py: 1.5
                                                }}
                                            >
                                                {cell.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((entry, idx) => (
                                        <TableRow
                                            key={idx}
                                            sx={{
                                                backgroundColor: "#ffffff",
                                                "&:hover": { backgroundColor: "#F8FAFC" },
                                                "&:last-child td": { borderBottom: 0 }
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: "13px", color: "#475569", py: 2, whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {formatDate(entry.changedOn)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "13px", color: "#0F172A", fontWeight: 500, whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {entry.changedBy || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "13px", color: "#0F172A", whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {entry.field || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "13px", color: "#334155", whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {entry.oldValue || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "13px", color: "#334155", whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {entry.newValue || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "13px", color: "#64748B", whiteSpace: "normal", wordBreak: "break-word" }}>
                                                {entry.actionNotes || "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            {/* Footer containing high-fidelity Figma Close button styled as standard secondary variant */}
            <DialogActions sx={{ borderTop: "1px solid #E2E8F0", px: 3, py: 2, justifyContent: "flex-end", bgcolor: "#ffffff" }}>
                <Button
                    variant="secondary"
                    onClick={onClose}
                    height="36px"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrackChangesModal;
